// Move stock to another place: the shared form behind Harvest on the scrap bin
// and Move on a scanned box. It posts one move and reports it; the host decides
// where it lives (a popover, a drawer, the scanner card).

import React, { useEffect, useRef, useState } from 'react';
import { buttonClass } from '../../components/ui/Button';
import { modalSelect } from '../../components/review/fieldStyles';
import { Alert } from '../../components/ui/Alert';
import Reveal from '../../components/ui/Reveal';
import { Field } from '../../components/ui/Field';
import { usePullsApi, type MoveDestination, type MoveResult } from './pullsApi';

export interface MoveFormProps {
  receiptItemId: string;
  /** What is in the box now: the quantity's default and ceiling. */
  remaining: number;
  /** Destination to preselect when it is on offer (the shelf, for Harvest). */
  preferType?: string;
  submitLabel?: string;
  onMoved: (result: MoveResult, destination: MoveDestination) => void;
  onCancel?: () => void;
}

const MoveForm: React.FC<MoveFormProps> = ({
  receiptItemId,
  remaining,
  preferType,
  submitLabel = 'Move',
  onMoved,
  onCancel,
}) => {
  const api = usePullsApi();
  const [destinations, setDestinations] = useState<MoveDestination[] | null>(null);
  const [toId, setToId] = useState('');
  const [qty, setQty] = useState(String(remaining));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    let live = true;
    api
      .destinations()
      .then((r) => {
        if (!live) return;
        const list = r.locations || [];
        setDestinations(list);
        const preferred = preferType ? list.find((l) => l.type === preferType) : undefined;
        if (preferred) setToId(String(preferred.id));
      })
      .catch((err: any) => {
        if (live) {
          setDestinations([]);
          setError(err?.message || 'The list of places did not load. Close this and try again.');
        }
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (destinations) selectRef.current?.focus();
  }, [destinations]);

  const n = Number(qty);
  const valid = toId !== '' && Number.isInteger(n) && n >= 1 && n <= remaining;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || busy || !destinations) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.move({
        receipt_item_id: receiptItemId,
        quantity: n === remaining ? undefined : n,
        to_location_id: Number(toId),
      });
      const dest = destinations.find((d) => d.id === Number(toId))!;
      onMoved(result, dest);
    } catch (err: any) {
      setError(err?.message || 'The move did not go through. Try again.');
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-2">
      <label className="block">
        <span className="mb-1 block text-xs text-on-surface-variant">Move to</span>
        <select
          ref={selectRef}
          value={toId}
          onChange={(e) => {
            setToId(e.target.value);
            setError(null);
          }}
          disabled={!destinations}
          className={`${modalSelect} m-0 ${toId === '' ? 'text-on-surface-variant' : ''}`}
        >
          <option value="">{destinations ? 'Choose a place' : 'Loading places…'}</option>
          {(destinations || []).map((d) => (
            <option key={d.id} value={d.id}>
              {d.parent_name ? `${d.parent_name} / ${d.name}` : d.name}
            </option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className="mb-1 block text-xs text-on-surface-variant">
          Quantity (up to {remaining})
        </span>
        <Field
          type="number"
          inputMode="numeric"
          min={1}
          max={remaining}
          step={1}
          value={qty}
          onChange={(e) => {
            const raw = e.target.value;
            setQty(raw !== '' && Number(raw) > remaining ? String(remaining) : raw);
            setError(null);
          }}
          aria-label="Quantity to move"
          className="m-0"
        />
      </label>
      <Reveal show={!!error}>
        <Alert variant="text">{error}</Alert>
      </Reveal>
      <div className="flex justify-end gap-2 pt-1">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className={buttonClass('quiet')}
          >
            Never mind
          </button>
        )}
        <button
          type="submit"
          disabled={!valid || busy}
          className={buttonClass('filled')}
        >
          {busy ? 'Moving…' : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default MoveForm;
